import React, { useEffect, useState } from 'react';
import { proxyImg } from '../lib/imgProxy';

const SLIDES = [
  'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/d9c1rmf3_RR5_9871.jpg',
  'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/m2d33ib3_RR5_0289-2.jpg',
  'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/fuj8855c_RR5_9616.jpg',
  'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/7mwkxrmh_1.jpg',
  'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/ruf1i4ls_RR5_9574.jpg',
  'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/8qe6phr2_DSCF4200-2.jpg',
  'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/kpyvuf6j_RR5_1273.jpg',
  'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/o05y40re_RR5_1198.jpg',
  'https://customer-assets.emergentagent.com/job_87e28a3d-66d8-4503-ad2a-42e59c9e6067/artifacts/0mjenzu3_RR5_0730.jpg',
];

// Every press quote / endorsement we have, with full attributions.
const TESTIMONIALS = [
  {
    quote: 'Whether you’re making your first film, launching a new career, or getting ready to take charge of your life, Be the Train will ignite the fire within you to make it happen.',
    author: 'Mel Robbins',
    role: '#1 New York Times Bestselling Author',
  },
  {
    quote: 'The path forward isn’t finding a gatekeeper. It’s becoming the engine yourself.',
    author: 'Ted Hope',
    role: 'Producer · Author, Hope For Film',
  },
  {
    quote: 'Dan and Selina have consistently pushed the boundaries of independent filmmaking, audience building and creator entrepreneurship with a healthy dose of hustle.',
    author: 'Jackie Brenneman',
    role: 'CEO, IFTA & AFM',
  },
  {
    quote: 'Even in my fourth decade as a professional filmmaker, I learned important essentials from Be The Train.',
    author: 'Michael Miner',
    role: 'Co-writer, RoboCop · Director, The Book Of Stars',
  },
  {
    quote: 'Selina is an undeniable force of nature. I wish I had read this book when I was starting out as a young filmmaker in Hollywood.',
    author: 'Amy Wang',
    role: 'Director, Slanted · Writer, Crazy Rich Asians 2',
  },
  {
    quote: 'A bold, no-nonsense guide for anyone ready to stop waiting and start building their success.',
    author: 'Diana Luna',
    role: 'Executive Director, NALIP',
  },
  {
    quote: 'Informative, inspiring and frequently hilarious. A must-read for any creative soul chasing a dream and eager to start driving their own train.',
    author: 'Dan Mirvish',
    role: 'Filmmaker · Founder, Slamdance',
  },
  {
    quote: 'I’m only halfway through but I feel inspired enough to do my own indie feature.',
    author: 'Angela Guice',
    role: '2x Emmy Winner · Founder, NuContext',
  },
  {
    quote: 'A masterclass in modern creative entrepreneurship. Required reading for anyone who refuses to wait their turn.',
    author: 'John Fithian',
    role: 'Former CEO, National Association of Theatre Owners',
  },
];

const IMAGE_MS = 7500;
const QUOTE_MS = 5500;

export const PersonalSlideshow = () => {
  const [imgIndex, setImgIndex] = useState(0);
  const [qIndex, setQIndex] = useState(0);

  useEffect(() => {
    const i1 = setInterval(() => setImgIndex((i) => (i + 1) % SLIDES.length), IMAGE_MS);
    const i2 = setInterval(() => setQIndex((i) => (i + 1) % TESTIMONIALS.length), QUOTE_MS);
    return () => {
      clearInterval(i1);
      clearInterval(i2);
    };
  }, []);

  // Warm-prefetch the slide *after* the next one so by the time it's needed,
  // it's already in the browser image cache. Avoids the "loading-in mid-fade"
  // softness on slides 2+.
  useEffect(() => {
    const upcoming = SLIDES[(imgIndex + 2) % SLIDES.length];
    const img = new Image();
    img.decoding = 'async';
    img.src = proxyImg(upcoming, { w: 1600, q: 88 });
  }, [imgIndex]);

  return (
    <section
      data-testid="personal-slideshow-section"
      className="relative overflow-hidden bg-black"
    >
      <div className="relative w-full h-[600px] sm:h-[680px] lg:h-[760px]">
        {/* Image slides — only render the current + next slide to keep
            initial download small. We swap the underlying src as the
            index advances; CSS handles the crossfade. */}
        {SLIDES.map((src, i) => {
          // Render slide i only when it's the current one OR the next one
          const isCurrent = i === imgIndex;
          const isNext = i === (imgIndex + 1) % SLIDES.length;
          if (!isCurrent && !isNext) return null;
          return (
            <div
              key={i}
              className={`absolute inset-0 transition-opacity duration-[1400ms] ease-out ${
                isCurrent ? 'opacity-100' : 'opacity-0'
              }`}
              aria-hidden={isCurrent ? 'false' : 'true'}
            >
              <img
                src={proxyImg(src, { w: 1600, q: 88 })}
                alt=""
                className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[8000ms] ease-out ${
                  isCurrent ? 'scale-[1.06]' : 'scale-100'
                }`}
                loading="eager"
                fetchPriority={isCurrent ? 'high' : 'low'}
                decoding="async"
              />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-r from-black/45 via-black/55 to-black/85" />
              <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-black/40" />
            </div>
          );
        })}

        {/* Overlay */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 h-full flex items-end lg:items-center">
          <div className="w-full lg:max-w-lg lg:ml-auto pb-12 lg:pb-0">
            <p className="text-[#00A86B] text-[10px] sm:text-[11px] uppercase tracking-[0.4em] mb-5">
              The Movement
            </p>
            <h2
              className="text-2xl sm:text-3xl lg:text-[2.6rem] font-bold text-white leading-[1.15] mb-7 max-w-[40rem]"
              style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}
              data-testid="slideshow-heading"
            >
              Be The Train is a creative accelerator for filmmakers and storytellers who are done asking for permission — and ready to build careers that move.
            </h2>

            {/* Rotating quotes */}
            <div className="relative min-h-[210px] sm:min-h-[200px]">
              {TESTIMONIALS.map((t, i) => (
                <blockquote
                  key={i}
                  className={`absolute inset-0 transition-opacity duration-700 ${
                    i === qIndex ? 'opacity-100' : 'opacity-0 pointer-events-none'
                  }`}
                  data-testid={`slideshow-quote-${i}`}
                >
                  <p className="text-white/85 text-base sm:text-lg leading-relaxed mb-4">
                    “{t.quote}”
                  </p>
                  <footer>
                    <p className="text-white font-semibold text-sm">{t.author}</p>
                    <p className="text-white/50 text-xs leading-snug mt-0.5">{t.role}</p>
                  </footer>
                </blockquote>
              ))}
            </div>

            {/* Image dots */}
            <div className="flex items-center gap-2 mt-6">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setImgIndex(i)}
                  aria-label={`Show slide ${i + 1}`}
                  data-testid={`slideshow-dot-${i}`}
                  className={`h-[3px] transition-all duration-500 ${
                    i === imgIndex ? 'w-10 bg-[#00A86B]' : 'w-5 bg-white/30 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PersonalSlideshow;
